import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  RefreshCw,
  ExternalLink,
  Share2,
  Trash2,
  Loader2,
  Clock,
  Lock,
  Users,
  UserCheck,
  GitBranch,
  Calendar,
  Activity,
  Copy,
  Check,
  Server,
  Cpu,
  HardDrive,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  X,
  Globe,
  Pencil,
  Bell,
  Link2,
  Info,
  LayoutTemplate,
} from 'lucide-react'
import FileBrowserPanel from '../components/workspace/FileBrowserPanel'
import SourceReleasePanel from '../components/source-release/SourceReleasePanel'
import AppSecretsSection from '../components/AppSecretsSection'
import EditAppInfoModal from '../components/EditAppInfoModal'
import ShareModalComponent from '../components/ShareModal'
import TransferOwnershipSection from '../components/TransferOwnershipSection'
import CloneAppModal from '../components/CloneAppModal'
import { Switch } from '../components/ui/Switch'
import {
  getApp as fetchApp,
  startApp,
  stopApp,
  rebuildApp,
  deleteApp,
  updateApp,
  listAppShares,
  requestAccess,
  getAppResources,
  updateAppResources,
  resetAppResources,
  getAppLogs,
  getDeploymentStatus,
  stopProductionBuild,
  getProductionLogs,
  listAccessRequests,
  approveAccessRequest,
  denyAccessRequest,
  getPublicAccessRequestStatus,
  approvePublicAccessRequest,
  rejectPublicAccessRequest,
  type App,
  type AppShare,
  type AccessRequest,
  type PublicAccessRequestStatus,
  type MergedAppResources,
  type DeploymentStatus,
} from '../lib/api'
import { usePublishApp } from '../hooks/usePublishApp'
import { getCloneNotice, type CloneNotice } from '../lib/cloneFeedback'
import { useUser } from '../hooks/useUser'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useIsMobile } from '../hooks/useMediaQuery'
import { HelpLink } from '../components/HelpLink'
import AppUsageStats from '../components/analytics/AppUsageStats'
import VisitorTrendChart from '../components/analytics/VisitorTrendChart'
import InlineCategoryPicker from '../components/InlineCategoryPicker'
import AppActions from '../components/AppActions'
import { useFolders } from '../components/folders/useFolders'
import { withMountBase } from '../lib/mountBase'
import { formatBytes } from '../lib/format'
import { withCcBack, isMobileStandalonePwa } from '../lib/ccBack'

const statusConfig: Record<string, { color: string; textColor: string; label: string; pulse: boolean }> = {
  running: { color: 'bg-green-500', textColor: 'text-green-500', label: 'Running', pulse: false },
  stopped: { color: 'bg-gray-500', textColor: 'text-gray-400', label: 'Stopped', pulse: false },
  queued: { color: 'bg-blue-500', textColor: 'text-blue-500', label: 'Queued', pulse: true },
  building: { color: 'bg-blue-500', textColor: 'text-blue-500', label: 'Building...', pulse: true },
  installing: { color: 'bg-amber-500', textColor: 'text-amber-500', label: 'Installing Dependencies...', pulse: true },
  starting: { color: 'bg-amber-500', textColor: 'text-amber-500', label: 'Starting...', pulse: true },
  error: { color: 'bg-red-500', textColor: 'text-red-500', label: 'Error', pulse: false },
}

const deploymentSteps = ['queued', 'building', 'installing', 'starting', 'running'] as const

const statusDescriptions: Record<string, string> = {
  queued: 'Your app is queued for deployment',
  building: 'Setting up the container environment',
  installing: 'Installing dependencies (npm install / pip install)',
  starting: 'Starting your app process',
  // Deliberately factual: "container running" is true when deployment_phase
  // is `running`; "live and accessible" is a separate claim that depends on
  // the app's own HTTP behavior (the proxy may still 404 if the app didn't
  // build a static bundle, returned an error page, etc.). We surface the
  // HTTP-reachability claim only after the in-page probe (see
  // `previewReachable` state below) confirms a non-5xx, non-empty response.
  running: 'Container is running',
  stopped: 'App is not running',
}

const accessConfig = {
  personal: { icon: Lock, label: 'Private', description: 'Only you can access' },
  specific: { icon: UserCheck, label: 'Specific people', description: 'Only people you add' },
  link: { icon: Link2, label: 'Anyone with the link', description: 'Any platform user with the link' },
  platform: { icon: Users, label: 'Platform', description: 'All platform users can access' },
  public: { icon: Globe, label: 'Public', description: 'Anyone on the internet, no login required' },
}

const runtimeConfig = {
  node: { label: 'Node.js', color: 'bg-green-500' },
  python: { label: 'Python', color: 'bg-blue-500' },
  static: { label: 'Static', color: 'bg-purple-500' },
  auto: { label: 'Auto', color: 'bg-gray-500' },
}

function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function PublishToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 5000)
    return () => clearTimeout(t)
    // Re-arm only on a new message — onDismiss is intentionally stable-by-call-site;
    // including it would cancel the timer on every parent render during polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message])
  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg bg-purple-600 text-white shadow-lg border border-purple-500/50 animate-in slide-in-from-bottom-2">
      <Check className="w-4 h-4 shrink-0" />
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onDismiss} className="ml-2 p-1 hover:bg-white/10 rounded" aria-label="Dismiss">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

export default function AppDetail() {
  const { appName } = useParams<{ appName: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useUser()
  const isMobile = useIsMobile()

  const [app, setApp] = useState<App | null>(null)
  const [shares, setShares] = useState<AppShare[]>([])
  const [resources, setResources] = useState<MergedAppResources | null>(null)
  const [resourceDraft, setResourceDraft] = useState({
    cpu_limit: '',
    memory_limit: '',
    disk_gb: '',
  })
  const [resourcesSaving, setResourcesSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showAllShares, setShowAllShares] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [focusWorkRequest, setFocusWorkRequest] = useState(0)
  const [showRebuildConfirm, setShowRebuildConfirm] = useState(false)
  const [showResources, setShowResources] = useState(false)
  const [copied, setCopied] = useState(false)
  const [logs, setLogs] = useState<string>('')
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsExpanded, setLogsExpanded] = useState(false)
  const [logTab, setLogTab] = useState<'all' | 'build' | 'runtime'>('all')
  const [actionError, setActionError] = useState<string | null>(null)
  const [publishToast, setPublishToast] = useState<string | null>(null)
  const [cloneNotice, setCloneNotice] = useState<CloneNotice | null>(null)
  const [showEditInfo, setShowEditInfo] = useState(false)
  const [deploymentPhase, setDeploymentPhase] = useState<string | null>(null)
  // Tracks whether the dev preview URL actually serves a non-5xx response.
  // Container-running != app-responding (e.g. dashboard built but dist/ is
  // empty because `tsc && vite build` failed before vite ran). When this
  // is false but deploymentPhase is `running`, the status badge text
  // hedges from "live and accessible" to "container running, app not
  // responding at /".
  const [previewReachable, setPreviewReachable] = useState<boolean | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<{
    log_excerpt?: string; port?: number; resolution?: string
  } | null>(null)

  // Production build state
  const [productionStatus, setProductionStatus] = useState<string | null>(null)
  const [productionError, setProductionError] = useState<string | null>(null)
  const [productionLogs, setProductionLogs] = useState<{ build_logs: string; runtime_logs: string } | null>(null)
  const [productionLogsExpanded, setProductionLogsExpanded] = useState(false)
  const [productionLogsLoading, setProductionLogsLoading] = useState(false)
  const [copiedDevUrl, setCopiedDevUrl] = useState(false)
  // Secret-state lives inside <AppSecretsSection /> so the workspace SecretsPanel
  // and this page share one source of truth + behavior.

  // Access requests state
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([])
  const [requestActionLoading, setRequestActionLoading] = useState<number | null>(null)

  // Public-access request state (admin-only inline approval; mirrors the
  // owner-side access_requests UX). Backend gates the GET on can_manage_shares
  // so admins viewing any app can see the pending state.
  const [publicAccessRequest, setPublicAccessRequest] = useState<PublicAccessRequestStatus | null>(null)
  const [publicAccessActionLoading, setPublicAccessActionLoading] = useState(false)
  const [publicAccessReviewNote, setPublicAccessReviewNote] = useState('')

  const loadApp = useCallback(async () => {
    if (!appName) return
    try {
      const found = await fetchApp(appName)
      setApp(found)

      // Only fetch shares/resources for users who can edit (avoids unnecessary 403s)
      const myPerm = found.my_permission ?? 'owner'
      const canEditApp = ['owner', 'admin', 'edit'].includes(myPerm)

      if (canEditApp) {
        const [sharesData, resourcesData] = await Promise.all([
          listAppShares(appName).catch(() => []),
          getAppResources(appName).catch(() => null),
        ])
        setShares(sharesData)
        if (resourcesData) {
          setResources(resourcesData)
          setResourceDraft({
            cpu_limit: resourcesData.cpu_limit,
            memory_limit: resourcesData.memory_limit,
            disk_gb: String(resourcesData.disk_gb),
          })
        }
      } else {
        setShares([])
        setResources(null)
      }
    } catch (err) {
      console.error('Failed to load app:', err)
      setApp(null)
    } finally {
      setLoading(false)
    }
  }, [appName])

  useEffect(() => {
    loadApp()
  }, [loadApp])

  // Load access requests for owners
  const loadAccessRequests = useCallback(async () => {
    if (!appName) return
    try {
      const requests = await listAccessRequests()
      setAccessRequests(requests.filter(r => r.app_name === appName))
    } catch {
      // Silently ignore - user may not be owner
    }
  }, [appName])

  useEffect(() => {
    loadAccessRequests()
  }, [loadAccessRequests])

  // Load the current public-access request state (if any) so admins see
  // the inline pending approval section. Backend gates the endpoint on
  // can_manage_shares, so a 403 here is fine — non-managers don't need
  // to see this section anyway.
  const loadPublicAccessRequest = useCallback(async () => {
    if (!appName) return
    try {
      const { request } = await getPublicAccessRequestStatus(appName)
      setPublicAccessRequest(request)
    } catch {
      // Silent — not an admin/owner, or transient failure
    }
  }, [appName])

  useEffect(() => {
    loadPublicAccessRequest()
  }, [loadPublicAccessRequest])

  async function handleApprovePublicAccess() {
    if (!publicAccessRequest) return
    setPublicAccessActionLoading(true)
    try {
      const note = publicAccessReviewNote.trim() || undefined
      await approvePublicAccessRequest(publicAccessRequest.id, note)
      setPublicAccessReviewNote('')
      await Promise.all([loadApp(), loadPublicAccessRequest()])
    } catch (err) {
      console.error('Failed to approve public-access request:', err)
    } finally {
      setPublicAccessActionLoading(false)
    }
  }

  async function handleRejectPublicAccess() {
    if (!publicAccessRequest) return
    setPublicAccessActionLoading(true)
    try {
      const note = publicAccessReviewNote.trim() || undefined
      await rejectPublicAccessRequest(publicAccessRequest.id, note)
      setPublicAccessReviewNote('')
      await loadPublicAccessRequest()
    } catch (err) {
      console.error('Failed to reject public-access request:', err)
    } finally {
      setPublicAccessActionLoading(false)
    }
  }

  useEffect(() => {
    const state = location.state as { cloneNotice?: CloneNotice } | null
    if (!state?.cloneNotice) return

    setCloneNotice(state.cloneNotice)
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: null }
    )
  }, [location.hash, location.pathname, location.search, location.state, navigate])

  async function handleApproveRequest(requestId: number) {
    setRequestActionLoading(requestId)
    try {
      await approveAccessRequest(requestId)
      setAccessRequests(prev => prev.filter(r => r.id !== requestId))
      await loadApp() // Refresh shares
    } catch (err) {
      console.error('Failed to approve request:', err)
    } finally {
      setRequestActionLoading(null)
    }
  }

  async function handleDenyRequest(requestId: number) {
    setRequestActionLoading(requestId)
    try {
      await denyAccessRequest(requestId)
      setAccessRequests(prev => prev.filter(r => r.id !== requestId))
    } catch (err) {
      console.error('Failed to deny request:', err)
    } finally {
      setRequestActionLoading(null)
    }
  }

  // Compute the display phase: prefer polled phase > app.deployment_phase > app.status
  const displayPhase = deploymentPhase || app?.deployment_phase || app?.status || 'stopped'
  const isDeploying = ['queued', 'building', 'installing', 'starting'].includes(displayPhase)
  const resolvedErrorMessage = errorMessage || app?.error_message || null
  const storagePercent = resources?.storage_percent ?? app?.storage_percent ?? 0
  const storageOverLimit = Boolean(resources?.storage_over_limit ?? app?.storage_over_limit)
  const storageWarningLevel = storageOverLimit ? 'exceeded' : storagePercent >= 90 ? 'urgent' : storagePercent >= 75 ? 'warning' : null
  const storageUsedBytes = resources?.storage_used_bytes ?? app?.storage_used_bytes ?? 0
  const storageLimitBytes = resources?.storage_limit_bytes ?? app?.storage_limit_bytes ?? 0
  const errorTitle = resolvedErrorMessage?.toLowerCase().startsWith('app restart failed')
    ? 'Restart Error'
    : 'Deployment Error'

  // Compute production display status
  const prodStatus = productionStatus || app?.production_status || null
  const isProductionBuilding = prodStatus === 'building'

  const { startPublish: startPublishApp, modal: publishModal, isPublishing } = usePublishApp({
    onPublishStarted: async () => {
      setProductionStatus('building')
      setProductionError(null)
      setActionError(null)
      setPublishToast('Build started — your app is being published.')
      await loadApp()
    },
    onViewWorkDetails: () => setFocusWorkRequest((value) => value + 1),
  })

  // Poll deployment status during active deployment or production build
  const pollDeploymentStatus = useCallback(async () => {
    if (!appName) return
    try {
      const result: DeploymentStatus = await getDeploymentStatus(appName)
      setDeploymentPhase(result.deployment_phase)
      setErrorMessage(result.error_message)
      setErrorDetails(result.error_details)

      // Update production status from deployment-status response
      if (result.production_status !== undefined) {
        setProductionStatus(result.production_status ?? null)
        setProductionError(result.production_error_message ?? null)
      }

      // When deployment settles, refresh full app data and stop polling
      if (result.deployment_phase === 'running' || result.deployment_phase === 'error') {
        await loadApp()
        // Keep the phase set so UI shows final state until next action
      }

      // When production build settles, refresh full app data
      if (result.production_status && result.production_status !== 'building') {
        await loadApp()
      }
    } catch (err) {
      console.error('Failed to poll deployment status:', err)
      throw err
    }
  }, [appName, loadApp])

  useAutoRefresh(pollDeploymentStatus, {
    baseInterval: 2000,
    maxInterval: 5000,
    enabled: isDeploying || isProductionBuilding,
  })

  // Auto-expand logs on error and switch to build tab
  useEffect(() => {
    if (displayPhase === 'error') {
      setLogsExpanded(true)
      setLogTab('build')
    }
  }, [displayPhase])

  // Fetch logs (supports log type tabs)
  const fetchLogs = useCallback(async () => {
    if (!appName || !app) return
    const canFetchLogs = app.status === 'running' || displayPhase === 'error' || isDeploying
    if (!canFetchLogs) return
    setLogsLoading(true)
    try {
      const result = await getAppLogs(appName, 100, logTab)
      setLogs(result.logs || '')
    } catch (err) {
      console.error('Failed to fetch logs:', err)
      setLogs('')
      throw err
    } finally {
      setLogsLoading(false)
    }
  }, [appName, app, logTab, displayPhase, isDeploying])

  // Re-fetch logs when tab changes
  useEffect(() => {
    if (logsExpanded && app) {
      fetchLogs()
    }
  }, [logTab, logsExpanded, fetchLogs, app])

  // Live HTTP-reachability probe for the running app. Container-running is
  // not the same as serving a non-5xx response (e.g. when the dashboard
  // build failed before `vite build` and dist/ is empty -- the proxy 404s
  // even though the supervisord-managed nginx is up). Probe the preview URL
  // and surface the result in the status badge wording.
  useEffect(() => {
    if (displayPhase !== 'running' || !app?.url) {
      setPreviewReachable(null)
      return
    }
    let cancelled = false
    const controller = new AbortController()
    fetch(app.url, {
      method: 'HEAD',
      credentials: 'include',
      signal: controller.signal,
    })
      .then((r) => {
        if (cancelled) return
        // 2xx/3xx = reachable. 5xx + 404 with empty body (the
        // dist/-missing case) = not reachable. Anything else (401/403)
        // means the app responded -- count as reachable; auth UX is a
        // separate badge concern.
        setPreviewReachable(r.status < 500 && r.status !== 404)
      })
      .catch(() => {
        if (cancelled) return
        setPreviewReachable(false)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [displayPhase, app?.url, app?.updated_at])

  // Initial log fetch when conditions become true
  useEffect(() => {
    if ((app?.status === 'running' || displayPhase === 'error') && logsExpanded) {
      fetchLogs()
    }
  }, [app?.status, displayPhase, logsExpanded, fetchLogs])

  // Log polling with exponential backoff
  useAutoRefresh(fetchLogs, {
    baseInterval: 5000,
    maxInterval: 60000,   // 1 minute max backoff
    enabled: (app?.status === 'running' || displayPhase === 'error') && logsExpanded,
  })

  async function handleStart() {
    if (!app) return
    setActionLoading('start')
    setActionError(null)
    setDeploymentPhase('queued')
    setErrorMessage(null)
    setErrorDetails(null)
    try {
      const result = await startApp(app.name)
      if (result.deployment_phase) {
        setDeploymentPhase(result.deployment_phase)
      }
      await loadApp()
    } catch (err) {
      setDeploymentPhase(null)
      setActionError(err instanceof Error ? err.message : 'Failed to start app')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleStop() {
    if (!app) return
    setActionLoading('stop')
    setActionError(null)
    try {
      await stopApp(app.name)
      setDeploymentPhase(null)
      setErrorMessage(null)
      setErrorDetails(null)
      await loadApp()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to stop app')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleRebuild() {
    if (!app) return
    setActionLoading('rebuild')
    setActionError(null)
    setDeploymentPhase('queued')
    setErrorMessage(null)
    setErrorDetails(null)
    try {
      const result = await rebuildApp(app.name)
      if (result.deployment_phase) {
        setDeploymentPhase(result.deployment_phase)
      }
      await loadApp()
    } catch (err) {
      setDeploymentPhase(null)
      setActionError(err instanceof Error ? err.message : 'Failed to rebuild app')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDelete() {
    if (!app) return
    setActionLoading('delete')
    try {
      await deleteApp(app.name)
      navigate('/apps')
    } catch (err) {
      console.error('Failed to delete app:', err)
      setActionLoading(null)
    }
  }

  async function handleStopProduction() {
    if (!app) return
    setActionLoading('production-stop')
    setActionError(null)
    try {
      await stopProductionBuild(app.name)
      setProductionStatus('stopped')
      await loadApp()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to stop production')
    } finally {
      setActionLoading(null)
    }
  }

  async function fetchProductionLogs() {
    if (!appName) return
    setProductionLogsLoading(true)
    try {
      const result = await getProductionLogs(appName)
      setProductionLogs({ build_logs: result.build_logs, runtime_logs: result.runtime_logs })
    } catch (err) {
      console.error('Failed to fetch production logs:', err)
      setProductionLogs(null)
    } finally {
      setProductionLogsLoading(false)
    }
  }

  function copyUrl() {
    if (!app) return
    navigator.clipboard.writeText(app.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function copyDevPreviewUrl() {
    if (!app?.dev_preview_url) return
    navigator.clipboard.writeText(app.dev_preview_url)
    setCopiedDevUrl(true)
    setTimeout(() => setCopiedDevUrl(false), 2000)
  }

  const {
    folders,
    addAppToFolder,
    removeAppFromFolder,
    createFolderAndAddApp,
    getAppFolderIds,
  } = useFolders(app ? [app] : [], 'my-apps')

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!app) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-semibold mb-2">App not found</h2>
        <p className="text-muted-foreground mb-4">The app "{appName}" doesn't exist or you don't have access.</p>
        <Link to="/apps" className="text-primary hover:underline">
          Back to My Apps
        </Link>
      </div>
    )
  }

  // Access Required page for unauthorized users
  if (app.access_denied) {
    const handleAccessRequest = async () => {
      try {
        await requestAccess(app.name)
        await loadApp()
      } catch (err) {
        console.error('Failed to request access:', err)
      }
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-6 flex items-center justify-center">
            <Lock className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Access Required</h1>
          <p className="text-muted-foreground mb-2">
            You need permission to access this app
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            {app.display_name || app.name}
            {app.owner_name && <> by {app.owner_name}</>}
          </p>
          {app.access_level === 'specific' && !app.has_pending_request && (
            <button
              onClick={handleAccessRequest}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Request Access
            </button>
          )}
          {app.has_pending_request && (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>Access request pending</span>
            </div>
          )}
          {app.access_level === 'personal' && (
            <p className="text-sm text-muted-foreground">
              This is a private app
            </p>
          )}
          <button
            onClick={() => navigate('/apps')}
            className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  const status = statusConfig[displayPhase] || statusConfig.stopped
  const access = accessConfig[app.access_level as keyof typeof accessConfig] || accessConfig.personal
  const runtime = runtimeConfig[app.runtime as keyof typeof runtimeConfig] || runtimeConfig.auto
  const AccessIcon = access.icon
  // In the new per-app workspace container, the app code is at /home/coder/workspace/main
  const appFolder = `/home/coder/workspace/main`

  // Permission-based UI control
  const isOwner = app.is_owner ?? true  // Default to true for backwards compatibility
  const myPermission = app.my_permission ?? 'owner'  // Default to owner for backwards compatibility
  const canEdit = ['owner', 'admin', 'edit'].includes(myPermission)
  const canManageShares = canEdit  // Owner, admin, and editors can manage shares
  const canDelete = isOwner  // Only owner can delete
  const canManageSecrets = isOwner || user.isAdmin
  const canManageResources = user.isAdmin
  const actionPermission: 'owner' | 'admin' | 'edit' | 'view' =
    myPermission === 'owner' || isOwner ? 'owner'
    : myPermission === 'admin' || myPermission === 'edit' || myPermission === 'view' ? myPermission
    : 'view'

  // Permission label for non-owners
  const permissionLabels: Record<string, { label: string; color: string }> = {
    admin: { label: 'Admin Access', color: 'text-purple-500 bg-purple-500/10' },
    edit: { label: 'Edit Access', color: 'text-blue-500 bg-blue-500/10' },
    view: { label: 'View Only', color: 'text-muted-foreground bg-muted' },
    platform: { label: 'Platform Access', color: 'text-muted-foreground bg-muted' },
  }

  async function handleSaveResources() {
    if (!app || !resources || !canManageResources) return
    setResourcesSaving(true)
    try {
      const diskGb = Number(resourceDraft.disk_gb)
      const updated = await updateAppResources(app.name, {
        cpu_limit: resourceDraft.cpu_limit.trim() || undefined,
        memory_limit: resourceDraft.memory_limit.trim() || undefined,
        disk_gb: Number.isFinite(diskGb) ? diskGb : undefined,
      })
      setResources(updated)
      setResourceDraft({
        cpu_limit: updated.cpu_limit,
        memory_limit: updated.memory_limit,
        disk_gb: String(updated.disk_gb),
      })
    } finally {
      setResourcesSaving(false)
    }
  }

  async function handleResetResources() {
    if (!app || !canManageResources) return
    setResourcesSaving(true)
    try {
      await resetAppResources(app.name)
      const updated = await getAppResources(app.name)
      setResources(updated)
      setResourceDraft({
        cpu_limit: updated.cpu_limit,
        memory_limit: updated.memory_limit,
        disk_gb: String(updated.disk_gb),
      })
    } finally {
      setResourcesSaving(false)
    }
  }
  const permInfo = !isOwner ? permissionLabels[myPermission] : null

  return (
    <div>
      {/* Header — single row with flex-wrap: back + title + (actions + Open App) stay together */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate('/apps')}
          className="p-2 rounded-lg hover:bg-secondary transition-colors shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0 sm:min-w-[360px]">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
            {app.url ? (
              <a
                href={withCcBack(app.url)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  // In an installed mobile PWA, stay in the standalone window
                  // so the injected app-nav-bar can render. Desktop keeps the
                  // new-tab behavior (and right-click / middle-click still work).
                  if (isMobileStandalonePwa()) {
                    e.preventDefault()
                    window.location.href = withCcBack(app.url)
                  }
                }}
                className="text-xl sm:text-2xl font-bold hover:text-primary hover:underline transition-colors truncate"
              >
                {app.display_name || app.name}
              </a>
            ) : (
              <h1 className="text-xl sm:text-2xl font-bold truncate">{app.display_name || app.name}</h1>
            )}
            {canEdit && (
              <button title="Edit app name, description, and category" onClick={() => setShowEditInfo(true)} className="p-1 sm:p-1.5 rounded-lg hover:bg-secondary transition-colors shrink-0">
                <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground" />
              </button>
            )}
            {app.is_template && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">
                <LayoutTemplate className="w-3.5 h-3.5" />
                Template
              </span>
            )}
            <div className="shrink-0">
              <InlineCategoryPicker
                currentCategory={app.category || null}
                canEdit={canEdit}
                onCategoryChange={async (cat) => { await updateApp(app.name, { category: cat }); await loadApp() }}
              />
            </div>
            {permInfo && (
              <span className={`px-2 py-0.5 sm:py-1 rounded text-xs font-medium shrink-0 ${permInfo.color}`}>{permInfo.label}</span>
            )}
          </div>
          <p className="text-muted-foreground font-mono text-xs sm:text-sm truncate">/a/{app.name}/</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={app.url ? withCcBack(app.url) : undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (app.url && isMobileStandalonePwa()) {
                e.preventDefault()
                window.location.href = withCcBack(app.url)
              }
            }}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shrink-0"
            title="Open app in new tab"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">Open App</span>
          </a>
          <AppActions
            app={app}
            permission={actionPermission}
            onStart={canEdit ? handleStart : undefined}
            onStop={canEdit ? handleStop : undefined}
            onRebuild={canEdit ? async () => { setShowRebuildConfirm(true) } : undefined}
            onRename={canEdit ? () => setShowEditInfo(true) : undefined}
            onPublish={canEdit && !isProductionBuilding && !isPublishing ? () => { void startPublishApp(app) } : undefined}
            onClone={canEdit ? () => setShowCloneModal(true) : undefined}
            onShare={canEdit ? () => setShowShareModal(true) : undefined}
            onDelete={canDelete ? handleDelete : undefined}
            folders={folders}
            appFolderIds={getAppFolderIds(app.name)}
            onMoveToFolder={(folderId) => { void addAppToFolder(folderId, app.name) }}
            onRemoveFromFolder={(folderId) => { void removeAppFromFolder(folderId, app.name) }}
            onCreateFolderAndMove={(name) => { void createFolderAndAddApp(name, app.name) }}
            showDetails={false}
            showLabels={!isMobile}
            isPublishing={isProductionBuilding || isPublishing}
          />
        </div>
      </div>

      {actionError && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 flex items-center justify-between">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="p-1 hover:bg-destructive/20 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {isProductionBuilding && (
        <div className="mb-6 p-4 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin shrink-0" />
          <div>
            <div className="font-medium">Publishing your app…</div>
            <div className="text-sm opacity-80">This can take a few minutes. We'll update the status automatically.</div>
          </div>
        </div>
      )}

      {publishToast && (
        <PublishToast message={publishToast} onDismiss={() => setPublishToast(null)} />
      )}

      {cloneNotice && (
        <div
          className={`mb-6 p-4 rounded-lg border flex items-center justify-between gap-3 ${
            cloneNotice.tone === 'warning'
              ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20'
              : 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20'
          }`}
        >
          <div className="flex items-center gap-2.5">
            <Info className="w-4.5 h-4.5 shrink-0" />
            <span>{cloneNotice.message}</span>
          </div>
          <button
            onClick={() => setCloneNotice(null)}
            className="p-1 hover:bg-black/5 dark:hover:bg-white/10 rounded"
            aria-label="Dismiss clone notice"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Access Request Notification Banner */}
      {canManageShares && !app.is_template && accessRequests.length > 0 && (
        <div className="mb-6 p-3.5 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Bell className="w-4.5 h-4.5 text-amber-500 shrink-0" />
            <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
              {accessRequests.length} pending access {accessRequests.length === 1 ? 'request' : 'requests'}
            </span>
            <span className="text-xs text-amber-600/70 dark:text-amber-400/70 hidden sm:inline">
              — {accessRequests.map(r => r.requester_name || r.requester_email).join(', ')}
            </span>
          </div>
          <a
            href="#sharing"
            onClick={(e) => {
              e.preventDefault()
              document.querySelector('[data-section="sharing"]')?.scrollIntoView({ behavior: 'smooth' })
            }}
            className="text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 whitespace-nowrap"
          >
            Review requests &rarr;
          </a>
        </div>
      )}

      {/* Template info banner */}
      {app.is_template && canEdit && (
        <div className="mb-6 p-3.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <LayoutTemplate className="w-4.5 h-4.5 text-indigo-500 shrink-0" />
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-400">
              This app is a template in the gallery
            </span>
          </div>
          <Link
            to="/admin/templates"
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 whitespace-nowrap"
          >
            Edit Template Info &rarr;
          </Link>
        </div>
      )}

      {/* App not running banner for viewers */}
      {!canEdit && app.status !== 'running' && (
        <div className="mb-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
          <p className="text-sm text-amber-600 font-medium">
            This app is not currently running. The owner can start it from their dashboard.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {app.description ? (
            <div className="p-6 rounded-xl border border-border bg-card">
              <h2 className="text-lg font-semibold mb-2">Description</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">{app.description}</p>
            </div>
          ) : canEdit ? (
            <button
              onClick={() => setShowEditInfo(true)}
              className="w-full p-4 rounded-xl border border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors text-sm"
            >
              + Add a description
            </button>
          ) : null}

          {/* Status Card */}
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4">Status</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className={`w-4 h-4 rounded-full ${status.color}`} />
                {status.pulse && (
                  <div className={`absolute inset-0 w-4 h-4 rounded-full ${status.color} animate-ping`} />
                )}
              </div>
              <div>
                <p className={`font-semibold ${status.textColor}`}>{status.label}</p>
                <p className="text-sm text-muted-foreground">
                  {displayPhase === 'error'
                    ? (errorMessage || app.error_message || 'Deployment failed')
                    : (
                      displayPhase === 'running' && previewReachable === true
                        ? 'Your app is live and accessible'
                        : displayPhase === 'running' && previewReachable === false
                          ? 'Container is running, but the app is not responding at / (open the workspace to investigate)'
                          : (statusDescriptions[displayPhase] || 'App is not running')
                    )}
                </p>
              </div>
            </div>

            {storageWarningLevel && (
              <div className={`mt-4 rounded-lg border p-3 text-sm ${
                storageWarningLevel === 'exceeded'
                  ? 'border-red-500/30 bg-red-500/10 text-red-200'
                  : storageWarningLevel === 'urgent'
                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-100'
                    : 'border-amber-500/25 bg-amber-500/10 text-amber-100'
              }`}>
                <div className="flex items-start gap-2">
                  <HardDrive className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">
                      {storageWarningLevel === 'exceeded' ? 'Storage quota exceeded' : 'Storage quota warning'}
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      This app is using {formatBytes(storageUsedBytes)} of {formatBytes(storageLimitBytes)}
                      {storageOverLimit
                        ? '. It may be stopped until storage is cleaned up.'
                        : '. Clean up large files, caches, old builds, or app data before it reaches the quota.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Deployment Progress Stepper */}
            {(isDeploying || displayPhase === 'error') && (
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  {deploymentSteps.map((step, i) => {
                    const stepIndex = deploymentSteps.indexOf(displayPhase as typeof deploymentSteps[number])
                    const errorIndex = displayPhase === 'error'
                      ? deploymentSteps.indexOf(
                          (deploymentPhase && deploymentPhase !== 'error'
                            ? deploymentPhase
                            : app?.deployment_phase && app.deployment_phase !== 'error'
                              ? app.deployment_phase
                              : 'starting') as typeof deploymentSteps[number]
                        )
                      : -1
                    const activeIndex = displayPhase === 'error' ? errorIndex : stepIndex
                    const isComplete = i < activeIndex
                    const isCurrent = i === activeIndex
                    const isError = displayPhase === 'error' && isCurrent

                    return (
                      <div key={step} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${
                            isError ? 'bg-red-500' :
                            isComplete ? 'bg-green-500' :
                            isCurrent ? 'bg-blue-500 animate-pulse' :
                            'bg-gray-600'
                          }`} />
                          <span className={`text-[10px] mt-1 ${
                            isError ? 'text-red-500' :
                            isCurrent ? 'text-foreground font-medium' :
                            isComplete ? 'text-green-500' :
                            'text-muted-foreground'
                          }`}>
                            {step.charAt(0).toUpperCase() + step.slice(1)}
                          </span>
                        </div>
                        {i < deploymentSteps.length - 1 && (
                          <div className={`h-0.5 flex-1 mx-1 ${
                            isComplete ? 'bg-green-500' : 'bg-gray-700'
                          }`} />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Error Guidance Panel */}
          {displayPhase === 'error' && resolvedErrorMessage && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <h3 className="font-semibold text-red-500 mb-2">{errorTitle}</h3>
              <p className="text-sm text-muted-foreground mb-3">
                {resolvedErrorMessage}
              </p>
              {(errorDetails?.resolution || app.error_details?.resolution) && (
                <div className="p-3 rounded bg-muted/50">
                  <p className="text-sm font-medium mb-1">How to fix:</p>
                  <p className="text-sm text-muted-foreground">
                    {errorDetails?.resolution || app.error_details?.resolution}
                  </p>
                </div>
              )}
              {(errorDetails?.log_excerpt || app.error_details?.log_excerpt) && (
                <div className="mt-3 p-3 rounded bg-[#1a1a1a] font-mono text-xs max-h-32 overflow-y-auto">
                  <pre className="text-gray-300 whitespace-pre-wrap">
                    {errorDetails?.log_excerpt || app.error_details?.log_excerpt}
                  </pre>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-red-500/10">
                <HelpLink section="deployment">View deployment guide</HelpLink>
              </div>
            </div>
          )}

          {canEdit && (
            <SourceReleasePanel
              appName={app.name}
              appFolder={appFolder}
              canEdit={canEdit}
              liveStatus={prodStatus}
              liveError={productionError || app.production_error_message || null}
              lastSuccessfulBuild={app.last_successful_build}
              actionLoading={actionLoading}
              liveLogs={productionLogs}
              liveLogsExpanded={productionLogsExpanded}
              liveLogsLoading={productionLogsLoading}
              onOpenSession={(terminalUrl, chromeUrl) => {
                const workspacePath = withMountBase('/workspace', window.location.pathname)
                const appPath = withMountBase(`/a/${app.name}/`, window.location.pathname)
                const appUrl = `${window.location.origin}${appPath}`
                // Honor the chromeUrl returned by openAppSession (D-22, D-23 / UX-11) so the
                // workspace panel can open the embedded chrome on first render.
                const chromeParam = chromeUrl
                  ? `&chromeUrl=${encodeURIComponent(chromeUrl)}`
                  : ''
                window.location.href = `${workspacePath}?app=${encodeURIComponent(app.name)}&mode=terminal&folder=${encodeURIComponent(appFolder)}&url=${encodeURIComponent(appUrl)}&session=${encodeURIComponent(terminalUrl)}${chromeParam}`
              }}
              onStopLive={handleStopProduction}
              onToggleLiveLogs={() => {
                const newExpanded = !productionLogsExpanded
                setProductionLogsExpanded(newExpanded)
                if (newExpanded) {
                  fetchProductionLogs()
                }
              }}
              formatRelativeTime={formatRelativeTime}
              focusWorkRequest={focusWorkRequest}
            />
          )}

          {/* Container Info - only for edit users */}
          {canEdit && (
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Server className="w-5 h-5" />
              Container
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  displayPhase === 'running' ? 'bg-green-500/20 text-green-500' :
                  isDeploying ? 'bg-amber-500/20 text-amber-500' :
                  displayPhase === 'error' ? 'bg-red-500/20 text-red-500' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {status.label}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Container Name</span>
                <span className="font-mono text-sm text-primary">cc-app-{app.name}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Port</span>
                <span className="font-mono text-sm">{app.port}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Runtime</span>
                <span className="text-sm font-medium">{runtime.label}</span>
              </div>

              {/* Resource Allocation */}
              {resources && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Cpu className="w-3 h-3" /> CPU
                    </span>
                    <span className="font-mono text-sm">{resources.cpu_limit}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <HardDrive className="w-3 h-3" /> Memory
                    </span>
                    <span className="font-mono text-sm">{resources.memory_limit}</span>
                  </div>

                </>
              )}

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <GitBranch className="w-3 h-3" /> Git
                </span>
                <span className={`text-sm ${app.git_initialized ? 'text-green-500' : 'text-muted-foreground'}`}>
                  {app.git_initialized ? 'Initialized' : 'Not initialized'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Created
                </span>
                <span className="text-sm">{new Date(app.created_at).toLocaleDateString()}</span>
              </div>

              {app.last_accessed && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Last Accessed
                  </span>
                  <span className="text-sm">{formatRelativeTime(app.last_accessed)}</span>
                </div>
              )}

              {/* Server Logs with Tabs */}
              <div className="mt-4 pt-4 border-t border-border">
                <button
                  onClick={() => {
                    setLogsExpanded(!logsExpanded)
                    if (!logsExpanded) {
                      fetchLogs()
                    }
                  }}
                  className="flex items-center justify-between w-full mb-2"
                >
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Server Logs
                  </span>
                  <div className="flex items-center gap-2">
                    {logsLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                    {logsExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                </button>
                {logsExpanded && (
                  <>
                    {/* Log Type Tabs */}
                    <div className="flex gap-1 mb-2">
                      {(['all', 'build', 'runtime'] as const).map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setLogTab(tab)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            logTab === tab
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {tab === 'all' ? 'All Logs' : tab === 'build' ? 'Build' : 'Runtime'}
                        </button>
                      ))}
                    </div>
                    <div className="p-4 rounded-lg bg-[#1a1a1a] font-mono text-xs max-h-64 overflow-y-auto">
                      {displayPhase === 'stopped' && !logs ? (
                        <span className="text-gray-500">App is not running</span>
                      ) : logs ? (
                        <pre className="text-gray-300 whitespace-pre-wrap">{logs}</pre>
                      ) : logsLoading ? (
                        <span className="text-gray-500">Loading logs...</span>
                      ) : (
                        <span className="text-gray-500">No logs available</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          )}

          {/* App Files - only for edit users */}
          {canEdit && (
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              App Files
            </h2>
            <FileBrowserPanel
              standalone={true}
              appName={app.name}
            />
          </div>
          )}

          {/* Visitor Trends */}
          {appName && <div><VisitorTrendChart appName={appName} /></div>}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4">Details</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Runtime</p>
                  <p className="font-medium">{runtime.label}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <GitBranch className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Git</p>
                  <p className="font-medium">{app.git_initialized ? 'Initialized' : 'Not initialized'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">{new Date(app.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              {app.last_accessed && (
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Last accessed</p>
                    <p className="font-medium">{new Date(app.last_accessed).toLocaleString()}</p>
                  </div>
                </div>
              )}
              {(app.owner_name || app.owner_email) && (
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">Owner</p>
                    <p className="font-medium truncate" title={app.owner_email ?? undefined}>
                      {app.is_owner ? 'You' : (app.owner_name || app.owner_email)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* URL */}
          <div className="p-6 rounded-xl border border-border bg-card">
            {prodStatus === 'running' && app.production_url ? (
              <>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  URLs
                </h2>
                {/* Production URL (primary) */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-green-500 mb-1 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                    App URL
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={app.url}
                      readOnly
                      className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm font-mono"
                    />
                    
                      <a title="Open app URL in a new tab"
                        href={withCcBack(app.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          // In an installed mobile PWA, stay in the standalone
                          // window so the injected app-nav-bar can render (a new
                          // tab would escape into the system browser). Desktop
                          // keeps new-tab behavior. Mirrors the title/Open-App links.
                          if (isMobileStandalonePwa()) {
                            e.preventDefault()
                            window.location.href = withCcBack(app.url)
                          }
                        }}
                        className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    
                    
                      <button title="Copy app URL to clipboard"
                        onClick={copyUrl}
                        className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    
                  </div>
                </div>
                {/* Dev Preview URL - only for edit users */}
                {canEdit && app.dev_preview_url && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                      Dev Preview URL <span className="text-xs text-muted-foreground font-normal">(changes visible here)</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={app.dev_preview_url}
                        readOnly
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm font-mono"
                      />
                      
                        <a title="Open the development preview in a new tab"
                          href={app.dev_preview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      
                      
                        <button title="Copy the dev preview URL to clipboard"
                          onClick={copyDevPreviewUrl}
                          className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                        >
                          {copiedDevUrl ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold mb-4">App URL</h2>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={app.url}
                    readOnly
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm font-mono"
                  />
                  
                    <button title="Copy this app URL to clipboard"
                      onClick={copyUrl}
                      className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  
                </div>
              </>
            )}
          </div>

          {/* Sharing - only for edit users, hidden for templates */}
          {canEdit && !app.is_template && (
          <div data-section="sharing" className="p-6 rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Sharing</h2>
              {canManageShares && (
                <button
                  onClick={() => setShowShareModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border border-border hover:bg-secondary transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                <AccessIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">{access.label}</p>
                <p className="text-sm text-muted-foreground">{access.description}</p>
              </div>
            </div>
            {shares.length > 0 && canManageShares && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">Shared with:</p>
                <div className="space-y-2">
                  {(showAllShares ? shares : shares.slice(0, 5)).map((share) => (
                    <div key={share.id} className="flex items-center justify-between text-sm">
                      <span>{share.email}</span>
                      <span className="text-muted-foreground">{share.permission}</span>
                    </div>
                  ))}
                </div>
                {shares.length > 5 && (
                  <button
                    onClick={() => setShowAllShares(!showAllShares)}
                    className="mt-2 text-xs text-primary hover:underline"
                  >
                    {showAllShares ? 'Show less' : `Show ${shares.length - 5} more`}
                  </button>
                )}
              </div>
            )}
            {canManageShares && accessRequests.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="w-4 h-4 text-amber-500" />
                  <p className="text-sm font-medium">
                    Access Requests
                    <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                      {accessRequests.length}
                    </span>
                  </p>
                </div>
                <div className="space-y-2">
                  {accessRequests.map((req) => (
                    <div key={req.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-secondary/50">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{req.requester_name || req.requester_email}</p>
                        {req.requester_name && (
                          <p className="text-xs text-muted-foreground truncate">{req.requester_email}</p>
                        )}
                        <p className="text-[11px] text-muted-foreground">{formatRelativeTime(req.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => handleApproveRequest(req.id)}
                          disabled={requestActionLoading === req.id}
                          className="px-2.5 py-1 text-xs rounded-md bg-green-500/10 text-green-600 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                          title="Approve"
                        >
                          {requestActionLoading === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleDenyRequest(req.id)}
                          disabled={requestActionLoading === req.id}
                          className="px-2.5 py-1 text-xs rounded-md bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                          title="Deny"
                        >
                          Deny
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Public-access request — admin-visible inline approval. */}
            {user.isAdmin && publicAccessRequest?.status === 'pending' && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-4 h-4 text-amber-500" />
                  <p className="text-sm font-medium">Public-access request</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/40 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Submitted {formatRelativeTime(publicAccessRequest.created_at)}.
                    Approving makes this app accessible without login.
                  </p>
                  {publicAccessRequest.justification && (
                    <div className="text-sm whitespace-pre-wrap">
                      <span className="font-medium text-muted-foreground">Justification: </span>
                      {publicAccessRequest.justification}
                    </div>
                  )}
                  <input
                    type="text"
                    value={publicAccessReviewNote}
                    onChange={(e) => setPublicAccessReviewNote(e.target.value)}
                    placeholder="Optional review note (sent to requester)"
                    maxLength={2000}
                    className="w-full px-2.5 py-1.5 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleApprovePublicAccess}
                      disabled={publicAccessActionLoading}
                      className="px-2.5 py-1 text-xs rounded-md bg-green-500/10 text-green-600 hover:bg-green-500/20 transition-colors disabled:opacity-50 inline-flex items-center gap-1"
                    >
                      {publicAccessActionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                      Approve
                    </button>
                    <button
                      onClick={handleRejectPublicAccess}
                      disabled={publicAccessActionLoading}
                      className="px-2.5 py-1 text-xs rounded-md bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors disabled:opacity-50 inline-flex items-center gap-1"
                    >
                      {publicAccessActionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Transfer ownership — owner/admin can hand the app off from here too,
                without opening the Share modal. */}
            {(myPermission === 'owner' || user.isAdmin) && (
              <div className="mt-4 pt-4 border-t border-border">
                <TransferOwnershipSection
                  app={app}
                  canTransfer={myPermission === 'owner' || user.isAdmin}
                  onChanged={loadApp}
                />
              </div>
            )}
          </div>
          )}

          {/* Resources - only for edit users */}
          {canEdit && (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <button
              onClick={() => setShowResources(!showResources)}
              className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Server className="w-5 h-5 text-primary" />
                <div className="text-left">
                  <h3 className="font-semibold">Resources</h3>
                  <p className="text-sm text-muted-foreground">
                    {resources?.is_customized ? 'Custom configuration' : 'Using defaults'}
                  </p>
                </div>
              </div>
              {showResources ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
            </button>
            {showResources && resources && (
              <div className="p-4 pt-0 border-t border-border">
                <div className="space-y-3 pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Cpu className="w-4 h-4" /> CPU
                    </span>
                    <span>{resources.cpu_request} / {resources.cpu_limit}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <HardDrive className="w-4 h-4" /> Memory
                    </span>
                    <span>{resources.memory_request} / {resources.memory_limit}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Replicas</span>
                    <span>{resources.replicas_min} - {resources.replicas_max}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Disk</span>
                    <span className={resources.storage_over_limit ? 'text-red-500 font-medium' : ''}>
                      {formatBytes(resources.storage_used_bytes)} / {formatBytes(resources.storage_limit_bytes)}
                    </span>
                  </div>
                  {resources.storage_limit_bytes > 0 && (
                    <div className="space-y-2">
                      <div className="h-2 rounded-full bg-secondary overflow-hidden">
                        <div
                          className={`h-full ${resources.storage_over_limit ? 'bg-red-500' : 'bg-primary'}`}
                          style={{ width: `${Math.min(resources.storage_percent, 100)}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{resources.storage_percent.toFixed(1)}% used</span>
                        <span>{resources.disk_gb} GB configured disk</span>
                      </div>
                    </div>
                  )}
                  {canManageResources && (
                    <div className="space-y-3 rounded-lg border border-border bg-background/50 p-3">
                      <div className="grid gap-3 sm:grid-cols-3">
                        <label className="space-y-1 text-xs text-muted-foreground">
                          <span>CPU limit</span>
                          <input
                            value={resourceDraft.cpu_limit}
                            onChange={(event) => setResourceDraft(prev => ({ ...prev, cpu_limit: event.target.value }))}
                            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                            placeholder="1000m"
                          />
                        </label>
                        <label className="space-y-1 text-xs text-muted-foreground">
                          <span>Memory limit</span>
                          <input
                            value={resourceDraft.memory_limit}
                            onChange={(event) => setResourceDraft(prev => ({ ...prev, memory_limit: event.target.value }))}
                            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                            placeholder="2Gi"
                          />
                        </label>
                        <label className="space-y-1 text-xs text-muted-foreground">
                          <span>Storage quota GB</span>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={resourceDraft.disk_gb}
                            onChange={(event) => setResourceDraft(prev => ({ ...prev, disk_gb: event.target.value }))}
                            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                            placeholder="80"
                          />
                        </label>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={handleSaveResources}
                          disabled={resourcesSaving}
                          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                          {resourcesSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          Save limits
                        </button>
                        <button
                          onClick={handleResetResources}
                          disabled={resourcesSaving}
                          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-secondary disabled:opacity-50"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Reset to defaults
                        </button>
                      </div>
                    </div>
                  )}
                  {resources.storage_breakdown.length > 0 && (
                    <div className="space-y-2 rounded-lg border border-border bg-background/50 p-3">
                      {resources.storage_breakdown.map((item) => (
                        <div key={item.key} className="flex items-center justify-between gap-3 text-xs">
                          <span className="truncate text-muted-foreground" title={item.path || undefined}>
                            {item.label}
                          </span>
                          <span className="font-mono shrink-0">{formatBytes(item.bytes)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Health Checks</span>
                    <span>{resources.health_check_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Auto Restart</span>
                    <span>{resources.auto_restart ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
                {resources.is_customized && !canManageResources && (
                  <button
                    disabled
                    className="flex items-center gap-2 mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Custom limits set by an admin
                  </button>
                )}
              </div>
            )}
          </div>
          )}

          {/* Keep Alive - Only shown to users with edit permission */}
          {canEdit && (
            <div className="p-6 rounded-xl border border-border bg-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-primary" />
                  <div>
                    <h3 className="font-semibold">Keep alive</h3>
                    <p className="text-sm text-muted-foreground">
                      Prevent auto-shutdown after inactivity
                    </p>
                  </div>
                </div>
                <Switch
                  checked={app.keep_alive}
                  onCheckedChange={async (checked) => {
                    try {
                      const updated = await updateApp(app.name, { keep_alive: checked })
                      setApp(updated)
                    } catch (err) {
                      console.error('Failed to update keep alive:', err)
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* App secrets */}
          <AppSecretsSection appName={app.name} canManage={canManageSecrets} />

          {/* App Usage Stats */}
          {appName && <AppUsageStats appName={appName} />}

          {/* Danger Zone - Only shown to owner */}
          {canDelete && (
            <div className="p-6 rounded-xl border border-destructive/30 bg-destructive/5">
              <h2 className="text-lg font-semibold mb-4 text-destructive">Danger Zone</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Deleting this app removes it from your active list, but you can restore it for 30 days.
              </p>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete App
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {/* Edit App Info Modal */}
      {showEditInfo && (
        <EditAppInfoModal
          app={app}
          onClose={() => setShowEditInfo(false)}
          onSaved={(updated) => {
            setShowEditInfo(false)
            if (updated.name !== app.name) {
              navigate(`/apps/${updated.name}`, { replace: true })
            } else {
              setApp(updated)
            }
          }}
        />
      )}

      {showShareModal && (
        <ShareModalComponent
          app={app}
          onClose={() => setShowShareModal(false)}
          onShared={loadApp}
        />
      )}

      {/* Clone Modal */}
      {showCloneModal && (
        <CloneAppModal
          app={app}
          onClose={() => setShowCloneModal(false)}
          onCloned={(response) => {
            setShowCloneModal(false)
            navigate(`/apps/${response.name}`, {
              state: { cloneNotice: getCloneNotice(response) },
            })
          }}
        />
      )}

      {/* Rebuild Confirmation */}
      {showRebuildConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-500" />
              Rebuild App
            </h2>
            <p className="text-muted-foreground text-sm mb-4">
              This will rebuild your app from source and reinstall all dependencies.
              The app will be briefly unavailable during the build.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRebuildConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowRebuildConfirm(false); handleRebuild() }}
                disabled={!!actionLoading || isPublishing}
                className="flex-1 px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                Rebuild
              </button>
            </div>
          </div>
        </div>
      )}

      {publishModal}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">Delete "{app.name}"?</h2>
            <p className="text-muted-foreground mb-4">
              This will remove the app from active use and stop its containers. You can restore it for 30 days.
            </p>
            {app.is_template && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                This is an active template. Deleting it will remove it from the gallery.
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={!!actionLoading || isPublishing}
                className="flex-1 px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'delete' ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
